import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Menu, X, ChevronDown } from "lucide-react"
import { ThemeToggle } from "@/components/ThemeToggle"
import { Button } from "@/components/ui/button"
import { useTheme } from "@/providers/ThemeProvider"
import type { Navigation as NavigationType } from "@/types/content"

type NavigationProps = {
  navigation: NavigationType
}

export function Navigation({ navigation }: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null)
  const { theme } = useTheme()
  const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)

  return (
    <nav className="fixed top-0 w-full bg-background/80 backdrop-blur-md border-b border-border z-50">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3 font-bold text-xl hover:opacity-80 transition-opacity"
          onClick={(e) => {
            // If already on home page, scroll to top instead of navigating
            if (window.location.pathname === "/") {
              e.preventDefault()
              window.scrollTo({ top: 0, behavior: "smooth" })
            }
          }}
        >
          <img src="/images/logo.svg" alt="SapientsAI Logo" className="h-8 w-8 logo-icon" />
          <span className="hidden sm:flex">
            <span className="text-foreground">SAPIENTS</span>
            <span className="text-purple-500">AI</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navigation.items.map((item) => {
            // Handle dropdown items
            if (item.children) {
              return (
                <div key={item.label} className="relative group">
                  <button className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                    {item.label}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <div
                    className="absolute top-full left-0 mt-2 w-56 border border-border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[60]"
                    style={{
                      backgroundColor: isDark ? "rgb(15, 23, 42)" : "rgb(255, 255, 255)",
                    }}
                  >
                    <div className="py-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href!}
                          className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )
            }

            // Handle internal router links vs anchor links
            if (item.href?.startsWith("/")) {
              return (
                <Link
                  key={item.href}
                  to={item.href as "/" | "/about"}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </Link>
              )
            } else {
              return (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </a>
              )
            }
          })}
        </div>

        {/* Desktop Theme Toggle */}
        <div className="hidden md:block">
          <ThemeToggle />
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm md:hidden z-40"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Mobile Menu */}
          <div
            className="fixed top-[73px] right-0 w-64 border-l border-border shadow-xl md:hidden z-50 animate-slideInRight"
            style={{
              backgroundColor: isDark ? "rgb(15, 23, 42)" : "rgb(255, 255, 255)",
              height: "calc(100vh - 73px)",
            }}
          >
            <div className="flex flex-col p-6 space-y-4">
              {navigation.items.map((item) => {
                // Handle dropdown items
                if (item.children) {
                  return (
                    <div key={item.label}>
                      <button
                        className="text-base font-medium text-foreground hover:text-primary transition-colors py-2 flex items-center justify-between w-full"
                        onClick={() => setMobileDropdownOpen(mobileDropdownOpen === item.label ? null : item.label)}
                      >
                        {item.label}
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${mobileDropdownOpen === item.label ? "rotate-180" : ""}`}
                        />
                      </button>
                      {mobileDropdownOpen === item.label && (
                        <div className="ml-4 mt-2 space-y-2">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href!}
                              className="block text-sm font-medium text-muted-foreground hover:text-primary transition-colors py-2"
                              onClick={() => {
                                setMobileMenuOpen(false)
                                setMobileDropdownOpen(null)
                              }}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                }

                // Handle internal router links vs anchor links
                if (item.href?.startsWith("/")) {
                  return (
                    <Link
                      key={item.href}
                      to={item.href as "/" | "/about"}
                      className="text-base font-medium text-foreground hover:text-primary transition-colors py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )
                } else {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      className="text-base font-medium text-foreground hover:text-primary transition-colors py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  )
                }
              })}
            </div>
          </div>
        </>
      )}
    </nav>
  )
}
